/**
 * Strategy Status Page
 *
 * Displays active canary and blue-green deployment strategies.
 * Fetches real data via RPC when available, falls back to sample data.
 */

import { useState, useEffect } from 'react';
import { CanaryProgress, BlueGreenSlots } from '@brutal/brutal/organisms';
import type { CanaryProgressProps, BlueGreenSlot } from '@brutal/brutal/organisms';
import { getRpcClient } from '../lib/rpc-client';

// ═══════════════════════════════════════════════════════════════
// FALLBACK DATA (used when agent has no strategy.status capability)
// ═══════════════════════════════════════════════════════════════

const fallbackCanaryProgress: CanaryProgressProps = {
  current: 25,
  target: 75,
  status: 'ramping',
  traffic: {
    canary: 25,
    stable: 75,
  },
};

const fallbackBlueSlot: BlueGreenSlot = {
  color: 'blue',
  version: 'v2.0.0',
  traffic: 100,
  status: 'active',
};

const fallbackGreenSlot: BlueGreenSlot = {
  color: 'green',
  version: 'v2.1.0',
  traffic: 0,
  status: 'idle',
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function StrategyStatus() {
  const [activeTab, setActiveTab] = useState<'canary' | 'blue-green'>('canary');
  const [isLiveData, setIsLiveData] = useState(false);
  const [canaryData, setCanaryData] = useState<CanaryProgressProps>(fallbackCanaryProgress);
  const [blueSlot, setBlueSlot] = useState<BlueGreenSlot>(fallbackBlueSlot);
  const [greenSlot, setGreenSlot] = useState<BlueGreenSlot>(fallbackGreenSlot);

  useEffect(() => {
    const client = getRpcClient();
    if (client === null) return;

    let cancelled = false;

    async function fetchStrategy() {
      try {
        const result = await client!.call('strategy.status', null);
        if (cancelled) return;

        const data = result as {
          canary: CanaryProgressProps | null;
          blue: BlueGreenSlot | null;
          green: BlueGreenSlot | null;
        } | null;

        if (data !== null) {
          if (data.canary !== null) setCanaryData(data.canary);
          if (data.blue !== null) setBlueSlot(data.blue);
          if (data.green !== null) setGreenSlot(data.green);
          setIsLiveData(true);
        }
      } catch {
        // strategy.status not available on this agent — keep fallback
      }
    }

    void fetchStrategy();

    return () => {
      cancelled = true;
    };
  }, []);

  const tabStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? '#00ff8820' : 'transparent',
    color: isActive ? '#00ff88' : '#888',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontFamily: 'monospace',
    cursor: 'pointer',
  });

  return (
    <div>
      <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Strategy Status</h2>

      {!isLiveData && (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: '16px',
            backgroundColor: '#332800',
            borderRadius: '4px',
            color: '#ffaa00',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
          }}
        >
          Showing fallback data — connect to an agent with strategy.status capability for live data.
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        <button onClick={() => setActiveTab('canary')} style={tabStyle(activeTab === 'canary')}>
          Canary
        </button>
        <button
          onClick={() => setActiveTab('blue-green')}
          style={tabStyle(activeTab === 'blue-green')}
        >
          Blue-Green
        </button>
      </div>

      {/* Content */}
      {activeTab === 'canary' ? (
        <CanaryProgress {...canaryData} />
      ) : (
        <BlueGreenSlots
          blue={blueSlot}
          green={greenSlot}
          onSlotClick={(color) => console.log(`Clicked ${color} slot`)}
        />
      )}

      {/* Info */}
      <div
        style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          color: '#666',
          fontSize: '0.875rem',
        }}
      >
        <p style={{ margin: 0 }}>
          {isLiveData
            ? 'Live data from connected agent.'
            : '\uD83D\uDCA1 This view shows active deployment strategies. Connect to an agent with active canary or blue-green deployments to see real data.'}
        </p>
      </div>
    </div>
  );
}
